import { cacheKeys } from "@constants/cache-keys";
import { usePollUserPublicMetadata } from "@features/auth/hooks/usePollUserPublicMetadata";
import { usePostAuthRedirectPath } from "@features/auth/hooks/usePostAuthRedirectPath";
import { AppBar } from "@features/dashboard/layouts/AppBar";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { switchExamContext } from "@features/redeem/helpers";
import { autoRedeemActiveCode } from "@features/redeem/helpers/auto-redeem";
import { signInWithClerkTicket } from "@helpers/auth-provider";
import { handleNavigateBack } from "@helpers/common";
import {
	captureErrorToSentry,
	serializeErrorForSentry,
} from "@helpers/sentry-logger";
import { useGlobalStore } from "@hooks/useGlobalStore";
import Loader from "@memorang/ui/components/Loader";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { refreshExamsListCache } from "api/exams-query";
import { Redirect, Stack, useLocalSearchParams } from "expo-router";
import { useCallback, useEffect, useMemo, useState } from "react";

const CallbackHeader = () => (
	<AppBar
		title=""
		variant="public"
		showLogo
		onPressBack={() => handleNavigateBack("/")}
	/>
);

const VerifyRoute = () => {
	const { token, exam_id, sku, source, auto_redeem } = useLocalSearchParams<{
		token?: string;
		exam_id?: string;
		sku?: string;
		source?: string;
		auto_redeem?: string;
	}>();

	const { isLoading: isOnboardingLoading, postAuthRedirectPath } =
		usePostAuthRedirectPath();

	const [hasError, setHasError] = useState(false);
	const [isSessionCreated, setIsSessionCreated] = useState(false);
	const setIsAuthFlowExamContextSwitching = useGlobalStore(
		(state) => state.setIsAuthFlowExamContextSwitching,
	);

	const pollQueryKey = useMemo(() => ["auth-verify-ticket", token], [token]);

	const { userLogicalId, isWaitingForPublicMetadata, hasTimedOut } =
		usePollUserPublicMetadata({
			enabled: isSessionCreated,
			queryKey: pollQueryKey,
		});
	const { handleSwitchExamContext } = useExamContext();
	const queryClient = useQueryClient();

	const createSession = useCallback(async () => {
		if (!token) {
			setHasError(true);
			return;
		}

		try {
			const success = await signInWithClerkTicket(token);
			if (!success) {
				captureErrorToSentry(
					new Error("Auth callback ticket sign-in incomplete"),
				);
				setHasError(true);
				return;
			}

			setIsSessionCreated(true);
		} catch (error) {
			captureErrorToSentry(new Error("Auth callback ticket sign-in failed"), {
				error: serializeErrorForSentry(error),
			});
			setHasError(true);
		}
	}, [token]);

	useEffect(() => {
		createSession();
	}, [createSession]);

	const shouldAutoRedeem = auto_redeem === "true";
	const shouldHandleExamContext = !!exam_id || shouldAutoRedeem;
	const isExamContextHandlingEnabled =
		isSessionCreated && !!userLogicalId && shouldHandleExamContext;

	useEffect(() => {
		setIsAuthFlowExamContextSwitching(shouldHandleExamContext);

		return () => {
			setIsAuthFlowExamContextSwitching(false);
		};
	}, [shouldHandleExamContext, setIsAuthFlowExamContextSwitching]);

	const { isSuccess: isExamContextHandled, isError: hasExamContextError } =
		useQuery({
			queryKey: [
				cacheKeys.authVerifyExamContext,
				token,
				userLogicalId,
				exam_id,
				sku,
			],
			queryFn: async () => {
				try {
					const redeemedExamId = shouldAutoRedeem
						? await autoRedeemActiveCode({
								sku,
								source: source ?? "auth_verify",
							})
						: null;

					if (redeemedExamId) {
						await refreshExamsListCache(queryClient);
					}

					const examId = redeemedExamId ?? exam_id;

					if (examId) {
						await switchExamContext(examId, handleSwitchExamContext);
					}

					return true;
				} catch (error) {
					captureErrorToSentry(
						new Error("Auth callback exam context handling failed"),
						{
							error: serializeErrorForSentry(error),
						},
					);
					throw error;
				} finally {
					setIsAuthFlowExamContextSwitching(false);
				}
			},
			enabled: isExamContextHandlingEnabled,
			staleTime: 0,
			gcTime: 0,
			retry: false,
		});

	const isWaitingForSession = !isSessionCreated || isWaitingForPublicMetadata;
	const isWaitingForExamContext =
		isExamContextHandlingEnabled &&
		!isExamContextHandled &&
		!hasExamContextError;
	const isLoading =
		!(hasError || hasTimedOut || hasExamContextError) &&
		(isWaitingForSession || isWaitingForExamContext || isOnboardingLoading);

	if (isLoading) {
		return (
			<>
				<Stack.Screen
					options={{ headerShown: true, header: () => <CallbackHeader /> }}
				/>
				<Loader />
			</>
		);
	}

	if (
		isSessionCreated &&
		userLogicalId &&
		(!shouldHandleExamContext || isExamContextHandled)
	) {
		return <Redirect href={postAuthRedirectPath} />;
	}

	return <Redirect href={"/auth/unauthorized"} />;
};

export default VerifyRoute;
